// @ts-nocheck - TODO: Fix complex dropdown types and state management
/* eslint-disable no-unused-vars, no-undef, no-unused-expressions */
import styled from '@emotion/styled';
import React, { Fragment, useEffect, useState, MouseEvent, ChangeEvent, forwardRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
	BsGlobe as Globe,
	BsThreeDotsVertical as ThreeDotsVertical,
	BsBoxArrowUpRight as BoxArrowUpRight,
	BsSearch as Search,
	BsSliders as Sliders,
	BsGear,
	BsLink45Deg,
	BsPencilFill,
	BsTrash3,
	BsXCircle,
	BsThreeDotsVertical as BsThreeDotsVerticalBs,
} from 'react-icons/bs';

import BayAddButton from '../../BayAddButton';
import BayCheckbox from '../../BayCheckbox';
import BayDropdown from '../../BayDropdown';
import BaySearch from '../../BaySearch';
import BayTooltip from '../../BayTooltip';
import isActingAsAnotherUser from './isActingAsAnotherUser';
import type { TranslationFunction } from '../../types/common';
import type { VoidCallback, BooleanCallback } from '../../types/callbacks';
import '../styles/quicklinks.scss';

// ===== Styled Components =====

const StyledDiv = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	width: 100%;
	align-items: flex-start;
	justify-content: flex-start;

	.dropdown-section {
		padding-top: 16px;
		border-top: 1px solid #e5e5e5;
		cursor: default;
		font-weight: 500;
		margin-bottom: 0.5rem;
		display: flex;
		justify-content: space-between;
		align-items: center;

		&:not(:first-of-type) {
			margin-top: 25px;
		}
	}
`;

const StyledList = styled.ul`
	list-style: none;
	padding-left: 0;
	width: 100%;
	margin-top: 10px;
	margin-bottom: 30px;

	> li {
		margin: 0;
		padding: 3px 0 8px 1rem;
		display: flex;
		justify-content: space-between;
		align-items: center;

		> svg {
			font-size: 18px;
		}
	}

	> li > a {
		padding: 0;
		font-size: 15px;
		font-weight: normal;
		font-stretch: normal;
		font-style: normal;
		letter-spacing: normal;
		background-color: unset;
		transition:
			0.25s all ease-out,
			0.25s all ease-out,
			0.25s all ease-out;
		text-decoration: none;
		color: var(--bs-dark);
		display: flex;
		align-items: center;
		max-width: 85%;

		> span {
			display: inline-block;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.bay-badge {
			display: inline-block;
			margin-left: 6px;
			text-decoration: none;
		}
	}

	> li > a:hover,
	> li > a:active {
		text-decoration: underline;
		transition:
			0.25s all ease-out,
			0.25s all ease-out,
			0.25s all ease-out;
	}
`;

const MenuListItems = styled.li`
	:hover {
		background-color: #f3f3f4;
	}
`;

// ===== Constants =====

const selectFilterType = [
	{ label: 'All ', value: 'All' },
	{ label: 'Personal ', value: 'Personal' },
	{ label: 'Company-wide ', value: 'Company' },
	{ label: 'SSO ', value: 'SSO' },
];

const bayLinks = [
	{
		linkHref: 'https://www.iqsupport.me/portal/en/kb/clientbay-support',
		label: 'cBayHelpDesk',
	},
	{
		linkHref: 'https://www.facebook.com/groups/210001779372182/',
		label: 'fbMastermindGroup',
	},
	{ linkHref: 'https://www.instagram.com/backatyou/', label: 'followOnInsta' },
	{
		linkHref: 'https://tcbackatyou.trainercentralsite.com/#/allsessions',
		label: 'registerForTraining',
	},
	{
		linkHref: 'https://support.backatyou.com/portal/en/kb/socialbay-support',
		label: 'sBayHelpDesk',
	},
];

const allFilterItems = ['All', 'Personal', 'Company', 'SSO'];

// ===== Interfaces =====

interface User {
	ActingAsACCESSLEVEL?: string;
	ActingAsPUBLICID?: string;
	PUBLICID?: string;
	ActingAsLICENSENUMBER?: string;
}

interface QuickLink {
	id: number;
	label: string;
	src: string;
	target?: string;
	publicid?: string;
	ssostatus?: string;
	singleSignOn_Parameters?: string;
	[key: string]: any;
}

interface SsoQuickLink {
	status: string;
}

interface QuickLinksDataObject {
	quicklinks?: QuickLink[];
	ssoquicklinks?: Record<string, SsoQuickLink>;
}

interface QuickLinkService {
	quickLinkLogin: (params: { sso_src: string }) => Promise<QuickLinkLoginResponse>;
	processPersonalAndWebLinks?: (params: any) => Promise<any>;
	processAgentSSO?: (params: any) => Promise<any>;
}

interface QuickLinkLoginResponse {
	STATUS: number;
	MESSAGE?: string;
	isSamlRequest?: boolean;
	URL?: string;
	LAYOUT?: string;
}

interface FilterItem {
	label: string;
	value: string;
}

interface ActionItem {
	eventKey: string;
	label: string;
	icon: React.ReactElement;
	textcolor?: string;
	onClick: VoidCallback;
}

interface BayLink {
	linkHref: string;
	label: string;
}

interface QuickLinksMenuProps {
	user: User;
	openModalFunc: (action: string, options: any, isUpdateSSOModal?: boolean) => void;
	quickLinksDataObject: QuickLinksDataObject;
	setFilterLabel: (labels: string[]) => void;
	FilterLabel: string[];
	iqQuickLinksEdit: (
		publicid: string,
		webSrc: string,
		ssoSrc: string,
		linkItem: QuickLink,
		mode: string,
		isCompanyWide?: boolean
	) => Promise<any>;
	searchLinks: string;
	setSearchLinks: (value: string) => void;
	t: TranslationFunction;
	showOffCanvas?: boolean;
	menuType?: string;
	focusSearch: boolean;
	setFocusSearch: BooleanCallback;
	setShowSnackbarHandler: (message: string, variant?: string) => void;
	enableTestimonialTree?: boolean;
	quickLinkService: QuickLinkService;
	linkArrayList: QuickLink[];
	showLaunchpadButton?: boolean;
	setShowFilterDropdown: BooleanCallback;
	showFilterDropdown: boolean;
}

interface ShortcutLinkProps {
	index: number;
	linkItem: QuickLink;
}

interface LinkActionsProps {
	index: number;
	linkItem: QuickLink;
	statusLink: string;
}

interface NavBayLinkProps {
	label: string;
	linkHref: string;
}

interface CustomToggleProps {
	children?: React.ReactNode;
	onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}

// ===== Component =====

const QuickLinksMenu = (props: QuickLinksMenuProps) => {
	const {
		user,
		openModalFunc,
		quickLinksDataObject,
		setFilterLabel,
		FilterLabel,
		iqQuickLinksEdit,
		searchLinks,
		setSearchLinks,
		t,
		showOffCanvas,
		menuType,
		focusSearch,
		setFocusSearch,
		setShowSnackbarHandler,
		enableTestimonialTree,
		quickLinkService,
		linkArrayList,
		showLaunchpadButton = false,
		setShowFilterDropdown,
		showFilterDropdown,
	} = props;

	const [loading, setLoading] = useState<boolean>(false);
	const [ssoLinksArray, setSsoLinksArray] = useState<Record<string, SsoQuickLink>>({});
	const [linksArray, setLinksArray] = useState<QuickLink[]>(linkArrayList);
	const isSearchDisabled = !quickLinksDataObject?.quicklinks?.length;

	useEffect(() => {
		setLoading(true);
		if (quickLinksDataObject?.quicklinks?.length) {
			setSsoLinksArray(quickLinksDataObject.ssoquicklinks ?? {});

			const filteredLinks = quickLinksDataObject.quicklinks.filter((link) => {
				//showing Testimonial tree based on the 'testimonialtree_enabletestimonialtree'
				const companyWebSsoCondn =
					(link.id === 1 &&
						link.src !== 'sso:28' &&
						link.ssostatus !== 'false') ||
					(link.src === 'sso:28' && enableTestimonialTree);
				const personalLinkCondn = link.id === 2 && !link.src.startsWith('sso');
				return (
					(companyWebSsoCondn || personalLinkCondn) &&
					link.label?.toLowerCase().includes(searchLinks?.toLowerCase())
				);
			});
			if (FilterLabel?.includes('All')) {
				setLinksArray(filteredLinks);
			} else {
				if (!FilterLabel?.length) {
					setLinksArray(filteredLinks);
				} else {
					const linksFiltered: QuickLink[] = [];
					const dupFilteredLinks = [...filteredLinks];
					dupFilteredLinks.forEach((link) => {
						if (
							FilterLabel?.includes('Personal') &&
							link.id === 2 &&
							!link.src.startsWith('sso')
						) {
							linksFiltered.push(link);
						}
						if (
							FilterLabel?.includes('Company') &&
							link.id === 1 &&
							!link.src.startsWith('sso')
						) {
							linksFiltered.push(link);
						}
						if (
							FilterLabel?.includes('SSO') &&
							link.id === 1 &&
							link.src.startsWith('sso')
						) {
							linksFiltered.push(link);
						}
					});
					setLinksArray(linksFiltered.sort((a, b) => a.id - b.id));
				}
			}
		} else {
			setLinksArray([]);
		}
		setLoading(false);
	}, [FilterLabel, quickLinksDataObject, searchLinks]);

	const setImage = (event: any) => {
		const greyGlobe =
			"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' width='1em' height='1em' fill='%23B9BABE' %3E%3Cpath d='M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5V1.077zM4.09 4a9.267 9.267 0 0 1 .64-1.539 6.7 6.7 0 0 1 .597-.933A7.025 7.025 0 0 0 2.255 4H4.09zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.49zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12H5.145zm.182 2.472a6.696 6.696 0 0 1-.597-.933A9.268 9.268 0 0 1 4.09 12H2.255a7.024 7.024 0 0 0 3.072 2.472zM3.82 11a13.652 13.652 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5H3.82zm6.853 3.472A7.024 7.024 0 0 0 13.745 12H11.91a9.27 9.27 0 0 1-.64 1.539 6.688 6.688 0 0 1-.597.933zM8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068H8.5zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.65 13.65 0 0 1-.312 2.5zm2.802-3.5a6.959 6.959 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5h2.49zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7.024 7.024 0 0 0-3.072-2.472c.218.284.418.598.597.933zM10.855 4a7.966 7.966 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4h2.355z'%3E%3C/path%3E%3C/svg%3E";
		if (
			(event.target.naturalHeight === 16 || event.target.naturalHeight === 0) &&
			event.target.src !== greyGlobe
		) {
			//handled for offline network - event.target.naturalHeight === 0
			event.target.src = greyGlobe;
		}
	};

	// eslint-disable-next-line no-unused-vars
	const LinkActions = ({ index, linkItem, statusLink }: LinkActionsProps) => {
		if (!linkItem) {
			return null;
		}

		const actionItems: ActionItem[] =
			linkItem?.id === 2
				? [
						{
							eventKey: 'edit',
							label: t('edit'),
							icon: <BsPencilFill className="bs-gray-700" />,
							onClick: () => {
								handleLinkAction('editPersonalLink', linkItem);
								iqQuickLinksEdit(
									linkItem?.publicid ?? '',
									linkItem?.src,
									'',
									linkItem,
									'edit',
								);
							},
						},
						{
							eventKey: 'delete',
							label: t('delete'),
							icon: <BsTrash3 className="bs-gray-700" />,
							onClick: () => {
								handleLinkAction('delete', linkItem);
							},
						},
					]
				: linkItem?.src?.startsWith('sso')
					? [
							{
								eventKey: 'edit',
								label: t('updateSSO'),
								icon: <BsPencilFill className="bs-gray-700" />,
								textcolor: '',
								onClick: async () => {
									const desc = t('updateMySSODesc');
									const hasEmptyFields = await handleEdit(linkItem);
									if (
										(statusLink === 'Disconnected' && hasEmptyFields) ||
										statusLink === 'Error'
									) {
										// Update my SSO Modal with prompt.
										statusHandleAction(
											'updatePersonalSso',
											linkItem,
											'Update SSO',
											statusLink,
											desc,
											true,
										);
									} else {
										// Update my SSO Modal without prompt.
										statusHandleAction(
											'updatePersonalSso',
											linkItem,
											'Update SSO',
											statusLink,
											desc,
										);
									}
								},
							},
							{
								eventKey:
									statusLink !== 'Disconnected' && statusLink !== 'Error'
										? 'disconnect'
										: 'reconnect',
								label:
									statusLink !== 'Disconnected' && statusLink !== 'Error'
										? t('disconnectSSO')
										: t('reconnectSSO'),
								icon:
									statusLink !== 'Disconnected' && statusLink !== 'Error' ? (
										<BsXCircle className="text-red-500" />
									) : (
										<BsLink45Deg className="text-green-500" />
									),
								textcolor:
									statusLink !== 'Disconnected' && statusLink !== 'Error'
										? 'text-red-500'
										: 'text-green-500',
								onClick: async () => {
									const desc =
										statusLink !== 'Disconnected'
											? t('disconnectDesc')
											: t('reconnectDesc');
									let label =
										statusLink !== 'Disconnected' && statusLink !== 'Error'
											? t('disconnectSSO')
											: t('reconnectSSO');
									const hasEmptyFields = await handleEdit(linkItem);
									// const hasEmptyFields = await getQuickLinkUserData(linkItem);
									if (
										(statusLink === 'Disconnected' && hasEmptyFields) ||
										statusLink === 'Error'
									) {
										const desc = t('disconnectDescError');
										label = t('unableToReconnect');
										// Open connect/reconnect modal with unable to proceed description.
										statusHandleAction(
											'connectionStatus',
											linkItem,
											label,
											statusLink,
											desc,
											true,
										);
									} else {
										// Open connect/reconnect modal.
										statusHandleAction(
											'connectionStatus',
											linkItem,
											label,
											statusLink,
											desc,
										);
									}
								},
							},
							...(user?.ActingAsACCESSLEVEL === 'A'
								? [
										{
											eventKey: 'managecompany',
											label: t('editCompanyWideLink'),
											icon: <BsGear className="bs-gray-700" />,
											textcolor: '',
											onClick: () => {
												//open drawer for edit companywide SSO handle bassed on the state.
												iqQuickLinksEdit(
													'global',
													linkItem?.src?.startsWith('sso:')
														? ''
														: linkItem?.src,
													linkItem?.src?.startsWith('sso:')
														? linkItem?.src
														: '',
													linkItem,
													'edit',
													true,
												);
											},
										},
									]
								: []),
						]
					: linkItem?.id === 1 && user?.ActingAsACCESSLEVEL === 'A'
						? [
								{
									eventKey: 'managecompany',
									label: t('editCompanyWideLink'),
									icon: <BsGear className="bs-gray-700" />,
									textcolor: '',
									onClick: () => {
										//open drawer for edit company wide link handle based on the state.
										iqQuickLinksEdit(
											'global',
											linkItem?.src,
											'',
											linkItem,
											'edit',
											true,
										);
									},
								},
							]
						: [];
		return (
			<BayDropdown
				disabled={actionItems?.length === 0}
				items={actionItems as any}
			>
				<BayTooltip
					disabled={!!actionItems?.length}
					overlay={t('disabledCompanyLinksTooltip')}
					overlayClassName="disabledDropdown"
					placement="topRight"
				>
					<div
						className={`${actionItems?.length ? 'option-icon-quickLinks cursor-pointer' : 'option-icon-quickLinks-disabled opacity-50'} rounded-1`}
					>
						<BsThreeDotsVerticalBs size={16} />
					</div>
				</BayTooltip>
			</BayDropdown>
		);
	};

	const handleClick = async (e: MouseEvent<HTMLAnchorElement>, value: string, LinkItem: QuickLink) => {
		e.preventDefault();
		e.stopPropagation();

		if (value === 'login') {
			if (isActingAsAnotherUser(user)) {
				setShowSnackbarHandler(t('denyAccessSsoToast'), 'danger');
				return;
			}
			const statusLink = ssoLinksArray?.[LinkItem?.src]?.status?.length
				? ssoLinksArray[LinkItem.src].status
				: 'Disconnected';
			const shouldOpenUpdateSSOModal =
				statusLink === 'Error' ||
				(statusLink === 'Disconnected' && (await handleEdit(LinkItem)));
			if (shouldOpenUpdateSSOModal) {
				// Required SSO info is missing - open the Update SSO modal so the user can fill it out.
				statusHandleAction(
					'updatePersonalSso',
					LinkItem,
					'Update SSO',
					statusLink,
					t('updateMySSODesc'),
					true,
				);
				return;
			}
			quickLinkService
				.quickLinkLogin({ sso_src: LinkItem?.src })
				.then((res) => {
					if (res?.STATUS === 0) {
						if (res?.isSamlRequest) {
							const url = String(res?.URL || '');
							if (!/^https?:\/\//i.test(url)) {
								setShowSnackbarHandler(t('invalidUrlNote'), 'danger');
								return;
							}
							const newTab = window.open(url, '_blank', 'noopener,noreferrer');
							if (newTab) newTab.opener = null;
							setShowSnackbarHandler(res?.MESSAGE ?? '');
							return;
						}
						const layout = document.createElement('div');
						layout.innerHTML = res.LAYOUT ?? '';
						document.body.appendChild(layout);
						const form = document.getElementById('quicklinks_form') as HTMLFormElement | null;
						form?.submit();
						form?.remove();
						setShowSnackbarHandler(res?.MESSAGE ?? '');
					} else {
						setShowSnackbarHandler(res?.MESSAGE ?? '', 'danger');
					}
				});
		} else {
			window.open(value);
		}
	};

	const ShortcutLink = ({ index, linkItem }: ShortcutLinkProps) => {
		const keys = Object.keys(ssoLinksArray);
		let statusLink = '';

		if (linkItem?.src?.includes('sso:')) {
			if (keys?.length > 0) {
				const key = linkItem?.src;
				if (ssoLinksArray[key]) {
					statusLink =
						ssoLinksArray[key].status === ''
							? 'Disconnected'
							: ssoLinksArray[key].status;
				} else {
					statusLink = 'Disconnected';
				}
			} else {
				statusLink = 'Disconnected';
			}
		} else {
			statusLink = '';
		}

		// No label so nothing to print
		if (linkItem?.label?.trim()?.length <= 0) {
			return null;
		}

		let element = null;
		let tempSrc = '';
		let tempLink = '';

		if (linkItem && !linkItem?.src?.startsWith('http')) {
			if (
				linkItem?.src?.startsWith('sso') &&
				linkItem?.singleSignOn_Parameters
			) {
				tempSrc =
					JSON.parse(linkItem?.singleSignOn_Parameters)?.web_src ??
					`http://${decodeURI(linkItem?.src)}`;
			} else {
				tempSrc = `http://${decodeURI(linkItem?.src)}`;
			}
		} else {
			tempSrc = decodeURI(linkItem?.src);
		}

		// 20220121 rjh - CB D'Ann Harper IABS quicklink request #2099007289
		//"{{licensenumber}}"
		if (/{{licensenumber}}/.test(tempSrc)) {
			if (user?.ActingAsLICENSENUMBER !== '') {
				tempSrc = tempSrc.replace(
					/{{licensenumber}}/g,
					user?.ActingAsLICENSENUMBER ?? '',
				);
			} else {
				tempSrc = tempSrc.replace(/{{licensenumber}}/g, '');
			}
		}

		if (linkItem?.target === 'FramePage') {
			tempLink = `/?page=framepage&src=${encodeURI(tempSrc)}`;
		} else if (linkItem?.target === '_blank') {
			tempLink = tempSrc;
		} else {
			tempLink = `/${encodeURI(tempSrc)}`;
		}

		element = (
			<MenuListItems className="mx-3 p-2 rounded-1">
				<div className="d-flex justify-content-start my-1 ps-2 align-items-center">
					{tempLink?.startsWith('http') ? (
						<img
							alt="Globe Icon"
							src={`https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${tempLink}&size=32`}
							style={{ height: 16, width: 16 }}
							onError={(e) => setImage(e)}
							onLoad={(e) => setImage(e)}
						/>
					) : (
						<Globe className="text-300" size={16} />
					)}
					{linkItem?.id === 1 && linkItem?.src?.startsWith('sso:') ? (
						<a
							className="linkLabel text-black-heading"
							href="#"
							onClick={(e) => handleClick(e, 'login', linkItem)}
						>
							<div className="mx-3 text-truncate ">{linkItem?.label}</div>
						</a>
					) : (
						<a
							className="linkLabel text-black-heading"
							href={tempLink}
							target={linkItem?.target === '_blank' ? '_blank' : ''}
						>
							<div className="mx-3 text-truncate ">{linkItem?.label}</div>
						</a>
					)}
				</div>

				<span className="my-1 pe-1 d-flex align-items-start">
					{linkItem?.src?.includes('sso:') ? (
						<a href="#" onClick={(e) => handleClick(e, 'login', linkItem)}>
							<BoxArrowUpRight className="me-2 text-700" size={16} />
						</a>
					) : (
						<a
							href={tempLink}
							target={linkItem?.target === '_blank' ? '_blank' : ''}
						>
							<BoxArrowUpRight className="me-2 text-700" size={16} />
						</a>
					)}
					{linkItem?.src === '' ||
					(linkItem?.src?.startsWith('http') &&
						linkItem?.id === 1 &&
						user?.ActingAsACCESSLEVEL !== 'A') ? (
						<BayTooltip
							overlay={t('disabledCompanyLinksTooltip')}
							overlayClassName="disabledDropdown"
							placement="topRight"
						>
							<span className="option-icon-quickLinks-disabled opacity-50">
								<ThreeDotsVertical size={16} />
							</span>
						</BayTooltip>
					) : (
						<LinkActions
							index={index}
							linkItem={linkItem}
							statusLink={statusLink}
						/>
					)}
				</span>
			</MenuListItems>
		);

		return element;
	};

	const NavBayLink = ({ label, linkHref }: NavBayLinkProps) => {
		return (
			<li className="mx-3 p-2 rounded-1 navBayLink">
				<a
					className="my-1 ps-2 text-decoration-none text-black-heading"
					href={linkHref}
					target="_blank"
				>
					<img
						alt="Globe Icon"
						src={`https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${linkHref}&size=32`}
						style={{ height: 16, width: 16 }}
						onError={(e) => setImage(e)}
						onLoad={(e) => setImage(e)}
					/>
					<span className="ms-3">{t(label)}</span>
				</a>
			</li>
		);
	};

	const handleLinkAction = async (action: string, linkItem: QuickLink) => {
		let dynamicObject: any = {};
		dynamicObject = {
			title: t('editPersonalLink'),
			description: t('editPersonalLinkDesc'),
			btnText: t('save'),
		};
		const editOptions = { ...linkItem, ...dynamicObject };
		openModalFunc(action, editOptions);
	};

	const statusHandleAction = async (
		action: string,
		linkItem: QuickLink,
		label: string,
		StatusType: string,
		description: string,
		isUpdateSSOModal: boolean = false,
	) => {
		let dynamicObject: any = {};
		dynamicObject = {
			title: label,
			description,
			btnText: `${action !== 'connectionStatus' ? t('update') : StatusType !== 'Disconnected' && StatusType !== 'Error' ? t('disconnect') : t('reconnect')}`,
			status: StatusType,
		};
		const editOptions = { ...linkItem, ...dynamicObject };
		openModalFunc(action, editOptions, isUpdateSSOModal);
	};

	const handleSelectType = (selected: string | string[]) => {
		if (Array.isArray(selected) ? selected?.length : selected) {
			let dynamicObject: any = {};
			if ((Array.isArray(selected) && selected.includes('Personal')) || selected === 'Personal') {
				dynamicObject = {
					id: 2,
					title: t('addPersonalLink'),
					description: t('addPersonalLinkDesc'),
					btnText: t('add'),
					modalType: selected,
				};
				openModalFunc('addPersonalLink', dynamicObject);
			}
		}
	};

	const handleFilterType = (item: FilterItem) => {
		let updatedLabel: string[];
		if (item?.value === 'All') {
			updatedLabel = [];
			if (!FilterLabel?.includes(item?.value)) {
				updatedLabel = allFilterItems;
			}
		} else if (FilterLabel?.length === 4) {
			updatedLabel = FilterLabel?.filter(
				(type) => type !== 'All' && type !== item?.value,
			);
		} else if (FilterLabel?.length === 2) {
			updatedLabel = FilterLabel?.includes(item?.value)
				? FilterLabel?.filter((type) => type !== item?.value)
				: allFilterItems;
		} else {
			updatedLabel = FilterLabel?.includes(item?.value)
				? []
				: [...FilterLabel, item?.value];
		}
		setFilterLabel(updatedLabel);
	};

	const handleEdit = async (linkItem: QuickLink): Promise<boolean> => {
		const data = await iqQuickLinksEdit(
			'global',
			linkItem?.src?.startsWith('sso:') ? '' : linkItem?.src,
			linkItem?.src?.startsWith('sso:') ? linkItem?.src : '',
			linkItem,
			'edit',
		);
		if (Object.keys(data).length) {
			const userParams = data?.ssoLogin_params?.length
				? data?.ssoLogin_params
				: '{}';
			const ssoDetails = JSON.parse(userParams)?.ssoDetails ?? {};
			// eslint-disable-next-line no-unused-vars, no-undefined
			const userKeyValuePairs = Object.keys(ssoDetails).length
				? Object.entries(ssoDetails)
					// eslint-disable-next-line no-unused-vars, no-undefined
					.filter(([key, value]: [string, any]) => value.access === 'U')
					.map(([key, value]: [string, any]) => ({ [key]: value.values }))
				: [];
			const hasEmptyFields = userKeyValuePairs.length
				? userKeyValuePairs.some((obj) =>
						Object.values(obj).some((val) => val === ''),
					)
				: true;
			return hasEmptyFields;
		} else {
			return true;
		}
	};

	// The forwardRef is important!!
	// Dropdown needs access to the DOM node in order to position the Menu
	const CustomToggle = forwardRef<HTMLButtonElement, CustomToggleProps>(({ children, onClick }, ref) => (
		<button
			ref={ref}
			className="d-flex align-items-center btn btn-outline-600 py-2 me-3"
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onClick(e);
				setShowFilterDropdown(!showFilterDropdown);
			}}
		>
			<Sliders className="sliderIcon" fill="var(--bs-gray-600)" size={16} />
			<span className="mx-2">{t('filter')}</span>
			{children}
		</button>
	));

	const searchEdit = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchLinks(e?.target.value);
		setFocusSearch(true);
	};

	const handleMenuToggle = (shown: boolean, meta: any) => {
		if (meta.source === 'rootClose') {
			setShowFilterDropdown(false);
		}
	};

	return (
		<div className="quicklinks-container">
			<div className="position-sticky bg-white mb-0 border-bottom quickLinks-stickyHeader">
				<div className="d-inline-flex flex-wrap justify-content-between w-100 px-3 py-2 p-sm-4 px-sm-5 border-bottom">
					<BayAddButton
						className="mb-2 me-3"
						text={<span>{`${t('addPersonal')}`}</span>}
						onClick={() => handleSelectType('Personal')}
					/>
					{user?.ActingAsACCESSLEVEL === 'A' && (
						<button
							className="btn btn-outline-600 mb-2"
							onClick={() =>
								(window.location.href = '/?page=company-wide-quick-links')
							}
						>
							<BsGear className="gearIcon" fill="var(--bs-gray-600)" size={16} />
							<span className="ps-2">{t('adminManagement')}</span>
						</button>
					)}
				</div>
				<div className="d-flex justify-content-between align-items-center p-3 p-sm-4 px-sm-5">
					<div
						className={`dropdown-filter ${isSearchDisabled ? 'pe-none' : ''} `}
					>
						{!quickLinksDataObject || isSearchDisabled ? (
							<button
								disabled
								className="btn btn-outline-600 d-flex align-items-center me-3 py-3"
								style={isSearchDisabled ? { width: 96 } : {}}
							>
								<Sliders className="sliderIcon" fill="var(--bs-gray-600)" size={16} />
								<span className="mx-2 ">{t('filter')}</span>
							</button>
						) : (
							<Dropdown
								show={showFilterDropdown}
								onToggle={(shown, meta) => handleMenuToggle(shown, meta)}
							>
								<Dropdown.Toggle
									as={CustomToggle}
									id="contacts_search_dropdown"
								>
									{FilterLabel?.length > 0 && (
										<>
											<span>|</span>
											<span className="mx-1">{FilterLabel?.length}</span>
										</>
									)}
								</Dropdown.Toggle>
								<Dropdown.Menu className="p-3 pb-0 border-bottom-0">
									{selectFilterType.map((val, i) => {
										return (
											<div
												key={i}
												className="d-flex align-items-center p-1 px-2"
											>
												<BayCheckbox
													id={`dropdown-checkbox-${i}`}
													isChecked={
														FilterLabel?.length &&
														FilterLabel?.includes(val.value)
													}
													label={
														<span className="text-capitalize">{val.label}</span>
													}
													name={val.label}
													onChangeFunc={() => {
														handleFilterType(val);
														if (document.activeElement !== document.body) {
															(document.activeElement as HTMLElement)?.blur();
														}
													}}
												/>
											</div>
										);
									})}
									<div
										className="my-2 p-1 px-2 bg-white cursor-pointer reset-button text-800"
										onClick={() => setFilterLabel(allFilterItems)}
									>
										{t('reset')}
									</div>
								</Dropdown.Menu>
							</Dropdown>
						)}
					</div>
					<div
						className={`quickLinks-search-container input-group ps-2 ${isSearchDisabled ? 'bg-100' : ''}`}
						style={isSearchDisabled ? { width: 180 } : {}}
					>
						<div className="search-icon d-flex align-items-center">
							<Search className="text-700 ms-2" size={16} />
						</div>
						<input
							ref={(input) => {
								if (
									showOffCanvas &&
									input &&
									menuType === 'links' &&
									focusSearch
								) {
									input.focus();
								}
							}}
							className={`form-control search-input quickLinkSearch text-500 ps-2 ${isSearchDisabled ? 'bg-100' : ''}`}
							disabled={isSearchDisabled}
							id="quickLink_filter_search"
							placeholder={t('searchLinks')}
							style={isSearchDisabled ? { width: 140 } : {}}
							value={searchLinks}
							onChange={searchEdit}
						/>
					</div>
				</div>
			</div>
			<StyledDiv className="quicklinks-links">
				<StyledList className="quicklinks-content">
					{loading ? (
						<li className="dropdown-section">{t('checkingForQl')}</li>
					) : (
						<>
							{linksArray?.length > 0 ? (
								linksArray
									?.sort((a, b) => a.label.localeCompare(b.label))
									.map((link, index) => {
										return (
											<Fragment key={index}>
												<ShortcutLink index={index} linkItem={link} />
											</Fragment>
										);
									})
							) : (
								<li className="quickLinks_empty_list justify-content-center">
									<div className="text-center">
										{!linksArray.length ? (
											searchLinks.length ? (
												<p>{t('noLinksFound')}</p>
											) : (
												<p>{t('anyLinksYet')}</p>
											)
										) : (
											''
										)}
										{user?.ActingAsACCESSLEVEL === 'A'
											? !searchLinks.length && (
													<p className="mx-4 text-center">
														{t('adminQlFilterDesc1')}
														<a
															className="text-blue pe-1"
															href="/?page=company-wide-quick-links"
														>
															{t('manageCompanyLinks')}
														</a>
														{t('adminQlFilterDesc2')}
													</p>
												)
											: ''}
									</div>
								</li>
							)}
						</>
					)}
				</StyledList>
			</StyledDiv>
			{!showLaunchpadButton && (
				<StyledDiv>
					<StyledList>
						<li className="dropdown-section px-5 fs-5 fw-bold">
							{t('backAtYouLinks')}
						</li>
						{bayLinks.map((baylink, index) => (
							<NavBayLink
								key={index}
								label={baylink.label}
								linkHref={baylink.linkHref}
							/>
						))}
					</StyledList>
				</StyledDiv>
			)}
		</div>
	);
};

export default QuickLinksMenu;
